import { LitElement, html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { api } from '../../api';
import { router } from '../../router';
import { showToast } from '../../components/ui/toast';
import { t } from '../../i18n';

interface PortalEvent {
  id: string; name: string; type_name: string | null; start_date: string | null; end_date: string | null;
  status: string; capacity: number | null; confirmed_count: number;
  my_status: string | null; my_payment_id: string | null; my_payment_status: string | null;
}

/** Student portal — events the student may take part in. Lists published events
 *  visible to them (to everyone, or by their tags), with their own status. */
@customElement('cadence-portal-events')
export class CadencePortalEvents extends LitElement {
  @state() private loading = true;
  @state() private events: PortalEvent[] = [];

  static styles = css`
    :host { display: block; }
    h1 { margin: 0 0 var(--space-4); font-size: 1.5rem; }
    .grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: var(--space-4); }
    .card { text-align: left; font: inherit; cursor: pointer; padding: var(--space-4); border: 1px solid var(--color-border); border-radius: var(--radius-md); background: var(--color-bg-primary); display: flex; flex-direction: column; gap: 8px; }
    .card:hover { border-color: var(--color-primary); }
    .name { font-weight: 700; font-size: 1.05rem; }
    .meta { color: var(--color-text-secondary); font-size: 0.85rem; }
    .badges { display: flex; gap: 6px; flex-wrap: wrap; margin-top: 2px; }
    .b { display: inline-block; padding: 2px 10px; border-radius: 999px; font-size: 0.74rem; font-weight: 600; }
    .b-none { background: var(--color-bg-secondary); color: var(--color-text-secondary); }
    .b-interested { background: #fef9c3; color: #854d0e; }
    .b-confirmed { background: #dcfce7; color: #166534; }
    .b-denied { background: #fee2e2; color: #991b1b; }
    .b-pay { background: #fee2e2; color: #991b1b; }
    .b-paid { background: #dbeafe; color: #1e40af; }
  `;

  connectedCallback(): void { super.connectedCallback(); this.load(); }

  private async load() {
    this.loading = true;
    try { this.events = await api.get<PortalEvent[]>('/portal/events'); }
    catch (e) { showToast((e as Error).message, 'error'); }
    finally { this.loading = false; }
  }

  private dates(e: PortalEvent) {
    if (!e.start_date) return t('portal.common.dateTbc');
    return e.end_date && e.end_date !== e.start_date ? `${e.start_date} → ${e.end_date}` : e.start_date;
  }

  private statusBadge(e: PortalEvent) {
    const map: Record<string, [string, string]> = {
      interested: ['b-interested', t('portal.events.pendingApproval')],
      confirmed: ['b-confirmed', t('portal.events.confirmed')],
      denied: ['b-denied', t('portal.events.notApproved')],
    };
    if (!e.my_status) return html`<span class="b b-none">${t('portal.events.notRegistered')}</span>`;
    const [cls, label] = map[e.my_status] ?? ['b-none', e.my_status];
    return html`<span class="b ${cls}">${label}</span>`;
  }

  private payBadge(e: PortalEvent) {
    if (e.my_status !== 'confirmed' || !e.my_payment_id) return '';
    return e.my_payment_status === 'paid'
      ? html`<span class="b b-paid">${t('portal.events.paid')}</span>`
      : html`<span class="b b-pay">${t('portal.events.paymentDue')}</span>`;
  }

  render() {
    if (this.loading) return html`<cadence-loading></cadence-loading>`;
    return html`
      <h1>${t('common.portalNav.events')}</h1>
      ${this.events.length
        ? html`<div class="grid">${this.events.map((e) => html`
            <button class="card" @click=${() => router.navigate(`/portal/events/${e.id}`)}>
              <span class="name">${e.name}</span>
              <span class="meta">${e.type_name ?? t('portal.events.eventFallback')} · ${this.dates(e)}</span>
              <span class="badges">${this.statusBadge(e)}${this.payBadge(e)}</span>
            </button>`)}</div>`
        : html`<cadence-empty-state heading=${t('portal.events.noEvents')} message=${t('portal.events.noEventsMessage')}></cadence-empty-state>`}
    `;
  }
}
